'use client'
import * as React from 'react'

import { isCommit } from '@basehub/data/replicache/index'
import { UseDiffResult, useDiff } from './use-diff'
import { useCommit } from '../../hooks-data/use-commit'
import { useCommitBlockTreeManager } from '~/hooks-data/blocks-tree'
import { useWorkingTreeStore } from '~/hooks-data/blocks-tree/provider'
import { useSelectedBranchOrCommitQuery } from '../commits/use-selected-branch-or-commit'
import { createStore, useStore } from 'zustand'
import { useClientDebugger } from '~/hooks/use-client-debugger'

const createWorkingDiffStore = (initial: UseDiffResult) => {
  return createStore<UseDiffResult>()(() => initial)
}

type WorkingDiffStore = ReturnType<typeof createWorkingDiffStore>

export const WorkingDiffContext = React.createContext<
  WorkingDiffStore | undefined
>(undefined)

export const WorkingDiffProvider = ({
  children,
}: {
  children?: React.ReactNode
}) => {
  const { data: selectedBranchOrCommit } = useSelectedBranchOrCommitQuery()

  const { headCommitId } = React.useMemo(() => {
    if (!selectedBranchOrCommit) return {}
    if (isCommit(selectedBranchOrCommit)) {
      return {}
    } else {
      return {
        headCommitId: selectedBranchOrCommit.headCommitId,
      }
    }
  }, [selectedBranchOrCommit])

  const { data, isLoading: isLoadingHeadCommit } = useCommit(headCommitId)
  const headCommit = data?.result

  const hasHeadCommit = isLoadingHeadCommit
    ? Boolean(headCommit) || undefined
    : Boolean(headCommit)

  const workingTree = useWorkingTreeStore((s) => s.tree)
  const { data: headCommitBlockTree } = useCommitBlockTreeManager({
    commitId: headCommit?.id,
    rootBlockId: headCommit?.rootBlockId ?? undefined,
    caller: 'use-working-diff',
  })

  const result = useDiff({
    treeNew: workingTree ?? undefined,
    treeOld:
      hasHeadCommit || hasHeadCommit === undefined
        ? (headCommitBlockTree as any)
        : false,
    treeCommonAncestor: false,
  })

  const { isDebugging } = useClientDebugger()

  if (isDebugging) {
    // eslint-disable-next-line no-console
    console.log('workingTree', workingTree)
    // eslint-disable-next-line no-console
    console.log('headCommitBlockTree', headCommitBlockTree)
    // eslint-disable-next-line no-console
    console.log('headCommit', headCommit)
    // eslint-disable-next-line no-console
    console.log('result', result)
  }

  const storeRef = React.useRef(createWorkingDiffStore(result))

  // keep store in sync with the result
  React.useEffect(() => {
    if (!result.diff) return
    storeRef.current.setState(result)
  }, [result])

  return (
    // eslint-disable-next-line react-compiler/react-compiler
    <WorkingDiffContext.Provider value={storeRef.current}>
      {children}
    </WorkingDiffContext.Provider>
  )
}

export function useWorkingDiff() {
  const store = React.useContext(WorkingDiffContext)
  if (!store) throw new Error('Missing WorkingDiffContext.Provider in the tree')
  return useStore(store)
}
